/**
 * Todas as rotinas conhecidas pelo sistema (para listagem e permissão de criar agendamento).
 * `permiteAgendamentoNoApp`: quando true, há registro em `task_agenda`, runner e tela de configuração.
 */
export type RotinaSistemaDef = {
  tipo: string;
  tituloPadrao: string;
  descricaoCurta: string;
  descricaoLonga: string;
  horarioPadraoTexto: string;
  insertPayload: Record<string, unknown>;
  permiteAgendamentoNoApp: boolean;
};

export const ROTINAS_SISTEMA: readonly RotinaSistemaDef[] = [
  {
    tipo: 'ordens_servico_sync',
    tituloPadrao: 'Sincronização de ordens de serviço',
    descricaoCurta: 'Importa OS da API Prospera para o banco local.',
    descricaoLonga:
      'Sincroniza ordens de serviço com a API. O disparo automático ocorre apenas com o sistema aberto, na janela de horário configurada.',
    horarioPadraoTexto: '20:00',
    insertPayload: { syncDaysRange: 14 },
    permiteAgendamentoNoApp: true,
  },
  {
    tipo: 'chamados_sync',
    tituloPadrao: 'Sincronização de chamados (API Prospera)',
    descricaoCurta: 'Importa chamados em duas etapas (abertos + encerrados recentes).',
    descricaoLonga:
      'Etapa 1: todos os status exceto finalizado e cancelado, sem filtro de data. Etapa 2: finalizados e cancelados com alteração de status nos últimos N dias (padrão 30). Disparo automático com o sistema aberto, na janela configurada.',
    horarioPadraoTexto: '08:00',
    insertPayload: { chamadosClosedDaysRange: 30 },
    permiteAgendamentoNoApp: true,
  },
  {
    tipo: 'reprocessamento_custos_sync',
    tituloPadrao: 'Reprocessamento de custos das OS',
    descricaoCurta: 'Recalcula custos no banco para o período configurado.',
    descricaoLonga:
      'Reprocessa custos das ordens de serviço já existentes no banco, conforme o intervalo de dias e o horário.',
    horarioPadraoTexto: '21:10',
    insertPayload: { custosDaysRange: 14 },
    permiteAgendamentoNoApp: true,
  },
  {
    tipo: 'reprocessamento_regras_desmembramento',
    tituloPadrao: 'Reprocessamento de regras de desmembramento (reclassificação)',
    descricaoCurta:
      'Reclassifica cliente filho pela soma mensal de horas executadas no bucket da regra; atualiza faturamento com valor/hora do cadastro.',
    descricaoLonga:
      'Lê `cliente_subdivisao_regras` (cliente pai, produto, tipo de projeto, operadores). Para cada mês-calendário (YYYY-MM) no período filtrado, soma `horas_totais` de todas as OS do mesmo bucket e aplica a condição (> ou ≤ limite, ex. 360 h) para escolher o cliente filho em todas as OS desse bucket. Em seguida grava `empresa_id` / `empresa_nome`, `valor_hora` (regra ou histórico do filho), `valor_faturamento` e `imposto`, como na correção manual — sem alterar `valor_a_pagar` (MOB). Resolve cliente pai mesmo quando a OS já está em um filho. Intervalo configurável por datas ou últimos N dias.',
    horarioPadraoTexto: '23:00',
    insertPayload: { regrasDaysRange: 30, regrasBatchSize: 50 },
    permiteAgendamentoNoApp: true,
  },
  {
    tipo: 'orcamentos_intranet_sync',
    tituloPadrao: 'Sincronização de orçamentos da Intranet',
    descricaoCurta: 'Importa a fila pendente da Intranet para o Portal Gestão.',
    descricaoLonga:
      'Busca orçamentos pendentes na API da Intranet, cria ou atualiza o orçamento local, sincroniza escopo e confirma o processamento de volta na Intranet.',
    horarioPadraoTexto: '07:45',
    insertPayload: { orcamentosImportBatchSize: 10 },
    permiteAgendamentoNoApp: true,
  },
  {
    tipo: 'fiscal_base_conhecimento_sync',
    tituloPadrao: 'Base de conhecimento fiscal',
    descricaoCurta: 'Atualiza CFOP, NCM, cClassTrib (IBS/CBS) e localidades do IBGE; verifica pacote novo de schemas da NF-e.',
    descricaoLonga:
      'Baixa as fontes oficiais e só regrava o que mudou (comparação por hash). Pacote novo de XSD não é aplicado sozinho: gera um alerta para revisão. Executada no servidor pelo worker fiscal (scripts/fiscal/worker-agenda.mjs) — não depende do sistema aberto.',
    horarioPadraoTexto: '03:00',
    insertPayload: { fontes: ['cfop', 'ncm', 'cclasstrib', 'ibge', 'xsd'] },
    permiteAgendamentoNoApp: true,
  },
  {
    tipo: 'monitor_sites_fiscais',
    tituloPadrao: 'Monitor de sites fiscais (WhatsApp)',
    descricaoCurta:
      'Lê o Portal da NF-e (notas técnicas e informes), o CGIBS e o Portal da Reforma Tributária, resume as novidades do dia e manda por WhatsApp.',
    descricaoLonga:
      'Executada no servidor pelo worker fiscal (scripts/fiscal/monitor/monitor-sites.mjs) — não depende do sistema aberto. A primeira leitura de cada fonte só registra o que já existe, sem avisar; daí em diante, o que aparecer pela primeira vez é novidade. Só manda mensagem quando há novidade: a IA resume o dia e o que for relevante para as regras fiscais vira proposta em Base de conhecimento › Novidades das fontes, para aprovação de uma pessoa (nada entra na base sozinho). Quem recebe é definido por usuário, na tela de Usuários (WhatsApp e assinatura deste agendamento).',
    horarioPadraoTexto: '07:00',
    insertPayload: {
      fontes: ['NFE_NOTAS_TECNICAS', 'NFE_INFORMES_TECNICOS', 'CGIBS', 'REFORMA_TRIBUTARIA_PORTAL'],
      webhookUrl: 'https://n8n.prosperasistemas.com/webhook/8a0693ae-8e1c-49c7-ae12-1e0b20f18cb3',
    },
    permiteAgendamentoNoApp: true,
  },
  {
    tipo: 'sincronizacao_clientes_api',
    tituloPadrao: 'Sincronização de clientes (API)',
    descricaoCurta: 'Mantém cadastro de clientes alinhado à API.',
    descricaoLonga:
      'Corresponde à aba de sincronização na tela de Clientes. Agendamento automático pelo app ainda não disponível.',
    horarioPadraoTexto: '07:30',
    insertPayload: {},
    permiteAgendamentoNoApp: false,
  },
] as const;

/** Tipos com `task_agenda` + runner — manter alinhado às entradas com `permiteAgendamentoNoApp: true`. */
export const TASK_AGENDA_AUTOMATION_TIPOS = [
  'ordens_servico_sync',
  'chamados_sync',
  'reprocessamento_custos_sync',
  'reprocessamento_regras_desmembramento',
  'orcamentos_intranet_sync',
  'fiscal_base_conhecimento_sync',
  'monitor_sites_fiscais',
] as const;

/** Rotinas executadas por worker de servidor (o browser só configura e pede "executar agora"). */
export const TASK_AGENDA_TIPOS_WORKER_SERVIDOR: readonly TaskAgendaAutomationTipo[] = [
  'fiscal_base_conhecimento_sync',
  'monitor_sites_fiscais',
];

export type TaskAgendaAutomationTipo = (typeof TASK_AGENDA_AUTOMATION_TIPOS)[number];

export function isTaskAgendaAutomationTipo(s: string | undefined): s is TaskAgendaAutomationTipo {
  return !!s && (TASK_AGENDA_AUTOMATION_TIPOS as readonly string[]).includes(s);
}

export function getRotinaSistema(tipo: string): RotinaSistemaDef | undefined {
  return ROTINAS_SISTEMA.find((r) => r.tipo === tipo);
}

/** Rotinas que podem ter registro em `task_agenda` e tela de detalhe. */
export function getRotinaAgendavel(tipo: string): RotinaSistemaDef | undefined {
  const r = getRotinaSistema(tipo);
  return r?.permiteAgendamentoNoApp ? r : undefined;
}

/** @deprecated use ROTINAS_SISTEMA ou getRotinaAgendavel */
export const TASK_AGENDA_ROTINA_CATALOGO = ROTINAS_SISTEMA.filter((r) => r.permiteAgendamentoNoApp);

/** @deprecated use getRotinaAgendavel */
export function getRotinaCatalogo(tipo: string): RotinaSistemaDef | undefined {
  return getRotinaAgendavel(tipo);
}

export const ROTINAS_COM_AGENDAMENTO_NO_APP = ROTINAS_SISTEMA.filter((r) => r.permiteAgendamentoNoApp);
